import React from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import { CONTRACTOR_SURFACE_RADIUS_MD_CLASS } from "../layoutRadius";
import type { ContractorSitePageProps } from "../types";

type ContactSectionProps = Pick<ContractorSitePageProps, "business">;

export const ContactSection: React.FC<ContactSectionProps> = ({ business }) => {
  const email = business.businessEmail?.trim();

  return (
    <section id="contact" className="bg-background py-20 sm:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          className="mb-10 text-center sm:text-left"
          kicker="Contact Us"
          title="Reach out"
          description="Have a question or want to discuss your project? Call or email us anytime."
        />

        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
              )}
            >
              <Phone className="h-5 w-5 text-accent" />
            </div>
            <div>
              <a
                href={`tel:${business.phone}`}
                className="text-sm font-medium text-foreground hover:underline"
              >
                {business.phone}
              </a>
              <div className="text-xs text-muted-foreground">
                Call us directly
              </div>
            </div>
          </div>

          {email ? (
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                <Mail className="h-5 w-5 text-accent" />
              </div>
              <div>
                <a
                  href={`mailto:${email}`}
                  className="text-sm font-medium text-foreground hover:underline"
                >
                  {email}
                </a>
                <div className="text-xs text-muted-foreground">
                  Email us anytime
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex items-start gap-4">
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
              )}
            >
              <MapPin className="h-5 w-5 text-accent" />
            </div>
            <div>
              <div className="text-sm font-medium text-foreground">
                {business.serviceArea}
              </div>
              <div className="text-xs text-muted-foreground">
                Primary service region
              </div>
            </div>
          </div>

          {business.businessHours.length > 0 ? (
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center bg-muted",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                <Clock className="h-5 w-5 text-accent" />
              </div>
              <div>
                <div className="text-sm font-medium text-foreground">
                  {business.businessHours[0]}
                </div>
                <div className="text-xs text-muted-foreground">
                  Business hours
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
